<template>
  <address v-if="hasAddress" class="sp-finder-result-address" part="base">
    <span v-if="addressName" class="sp-finder-result-address__name" translate="no">{{ addressName }}</span>
    <span v-if="isPlaceholderAddress" class="sp-finder-result-address__no-address" translate="no">
      {{ noAddressLabel }}
    </span>
    <template v-else-if="!isVirtualAddress">
      <div class="sp-finder-result-address__lines">
        <span v-if="result.street_address" translate="no">{{ result.street_address }}</span>
        <span v-if="cityStateZip" translate="no">{{ cityStateZip }}</span>
      </div>
    </template>
  </address>
</template>

<script setup>
import { computed } from "vue";
import { i18n } from "#ce/components/SpFinder/i18n";

// Direct instance access (see SpFinderResult.ce.vue) — useI18n() can't resolve
// the plugin across the custom-element app boundary.
function t(...args) {
  return i18n.global.t(...args);
}

const props = defineProps({
  result: {
    type: Object,
    default: () => ({}),
  },
  resultConfig: {
    type: Object,
    default: undefined,
  },
  labels: {
    type: Object,
    default: () => ({}),
  },
  resultType: {
    type: String,
    default: "provider",
    validator: (value) => ["provider", "location"].includes(value),
  },
});

const isLocation = computed(() => props.resultType === "location");

const isPlaceholderAddress = computed(() => props.result.placeholder_address);
const isVirtualAddress = computed(() => props.result.virtual_address);

// Locations suppress address_name when not virtual (the row already labels the place via the heading).
const addressName = computed(() => {
  if (isPlaceholderAddress.value || (isLocation.value && !isVirtualAddress.value)) {
    return "";
  }

  return props.result.address_name;
});
const cityStateZip = computed(() =>
  [props.result.city, [props.result.state, props.result.zip_code].filter(Boolean).join(" ")].filter(Boolean).join(", "),
);
const hasAddress = computed(() => props.resultConfig?.showProviderAddress);
const noAddressLabel = computed(() => props.labels.noAddress || t("no_address_available"));
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-finder-result-address {
  /* Layout */
  --address-gap: var(--sp-comp-finder-result-address-gap, var(--sp-ref-spacing-2));

  /* Color */
  --address-text-color: var(--sp-comp-finder-result-address-text-color, var(--sp-sys-color-on-surface));

  /* Typography */
  --address-name-font: var(--sp-comp-finder-result-address-name-font, var(--sp-sys-typescale-body-bold-short));

  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--address-gap);
  margin: 0;
  color: var(--address-text-color);
  font: var(--sp-sys-typescale-body-short, inherit);
  font-style: normal;

  &__name {
    font: var(--address-name-font);
  }

  &__lines {
    display: flex;
    flex-direction: column;
  }
}
</style>
